import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { webhooks } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Button, Group, Text, Badge } from '@repletoryreach/sdk-ui';
import { DataTable, Alert, EmptyState, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Menu, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Switch } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatDateTime, formatRelative } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';
import { RevealOnce } from '../../../components/RevealOnce';
import { DeliveriesDrawer } from './components/DeliveriesDrawer';
import './webhooks.css';

/**
 * Events the platform actually publishes.
 *
 * §4 item 11: the form was a free `Textarea` split on newlines and commas,
 * with the valid names documented nowhere, so a typo was silently accepted and
 * showed up later as "the webhook doesn't fire".
 */
const EVENT_GROUPS: { key: string; events: string[] }[] = [
  { key: 'whatsapp', events: ['message.received', 'message.sent', 'message.delivered', 'message.read', 'message.failed'] },
  { key: 'templates', events: ['template.submitted', 'template.approved', 'template.rejected'] },
  // SMS, email and broadcasts (4 October 2026): each under its own name, with the service's own payload.
  { key: 'sms', events: ['sms.received', 'sms.sent', 'sms.delivered', 'sms.failed'] },
  { key: 'email', events: ['email.sent', 'email.delivered', 'email.bounced', 'email.complained', 'email.failed'] },
  { key: 'broadcasts', events: ['broadcast.finished'] },
  // Bookings (7 October 2026): one event for each change to a booking's status.
  { key: 'bookings', events: ['booking.requested', 'booking.confirmed', 'booking.declined', 'booking.cancelled', 'booking.rescheduled', 'booking.completed'] },
  // Tickets (7 October 2026): opened, changed, solved and closed.
  { key: 'tickets', events: ['ticket.created', 'ticket.updated', 'ticket.solved', 'ticket.closed'] },
  { key: 'wallet', events: ['wallet.low', 'wallet.depleted', 'wallet.recovered', 'wallet.auto_topup', 'wallet.daily_spend'] }
];
const EVENT_TYPES = EVENT_GROUPS.flatMap((g) => g.events);

export default function SettingsWebhooksPage() {
  const t = useT();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<webhooks.WebhookSubscription | null>(null);
  const [inspecting, setInspecting] = useState<webhooks.WebhookSubscription | null>(null);
  const [freshSecret, setFreshSecret] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => webhooks.listWebhooks()
  });
  const rows = (data?.data ?? []) as Indexed<webhooks.WebhookSubscription>[];

  const setActive = useMutation({
    // §4 item 3: the status column rendered "Paused" and nothing could set it,
    // so during an incident the only option was deletion, which is destructive
    // AND rotates the signing secret.
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      webhooks.updateWebhook(id, { is_active: active }),
    onSuccess: (_r, { active }) => {
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      notifySuccess(active ? t('settings.webhooks.toast.resumed') : t('settings.webhooks.toast.paused'), t('settings.nav.webhooks'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.webhooks'))
  });

  const test = useMutation({
    mutationFn: (id: string) => webhooks.testSubscription(id),
    onSuccess: () => notifySuccess(t('settings.webhooks.toast.tested'), t('settings.nav.webhooks')),
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.webhooks'))
  });

  const del = useMutation({
    mutationFn: (w: webhooks.WebhookSubscription) => webhooks.deleteWebhook(w.id),
    onSuccess: (_r, w) => {
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      notifySuccess(t('settings.webhooks.toast.deletedNamed', { url: w.url }), t('settings.nav.webhooks'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.webhooks'))
  });

  function confirmDelete(w: webhooks.WebhookSubscription) {
    openConfirmModal({
      title: t('settings.webhooks.modal.delete.title'),
      children: (
        <Stack gap={8}>
          {/* §4 item 5: the confirm named nothing and did not mention that the
              signing secret dies with the subscription. */}
          <Text role="bodySmall">{t('settings.webhooks.modal.delete.body', { url: w.url })}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.webhooks.modal.delete.secret')}</Text>
        </Stack>
      ),
      labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => del.mutate(w)
    });
  }

  return (
    <Stack gap="md">
      {freshSecret && (
        <RevealOnce
          value={freshSecret}
          title={t('settings.webhooks.modal.secret.title')}
          description={t('settings.webhooks.modal.secret.body')}
          filename="repletoryreach-webhook-secret"
          onDismiss={() => setFreshSecret(null)}
        />
      )}

      <Card withBorder data-guide="webhooks">
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Title order={4}>{t('settings.webhooks.title')}</Title>
            <Button size="sm" onClick={() => setCreating(true)}>{t('settings.webhooks.actions.add')}</Button>
          </Group>

          {error && <Alert color="error" role="alert">{t('settings.webhooks.loadError')}</Alert>}

          {!isPending && !error && rows.length === 0 ? (
            <EmptyState
              title={t('settings.webhooks.empty')}
              description={t('settings.webhooks.empty.hint')}
              action={<Button size="xs" onClick={() => setCreating(true)}>{t('settings.webhooks.actions.add')}</Button>}
            />
          ) : (
            <DataTable<Indexed<webhooks.WebhookSubscription>>
              records={rows}
              loading={isPending}
              idAccessor="id"
              emptyState={t('settings.webhooks.empty')}
              columns={[
                {
                  accessor: 'url',
                  title: t('settings.webhooks.columns.url'),
                  render: (w) => <span className="rr-webhooks__url">{w.url}</span>
                },
                {
                  accessor: 'event_types',
                  title: t('settings.webhooks.columns.events'),
                  // §4 item 10: truncated to three with no "+N", so a
                  // subscription to eight events looked like three.
                  render: (w) => {
                    const shown = (w.event_types ?? []).slice(0, 3);
                    const rest = (w.event_types ?? []).length - shown.length;
                    return (
                      <Group gap={4} wrap="wrap">
                        {shown.map((e) => <Badge key={e} size="xs" variant="light">{e}</Badge>)}
                        {rest > 0 && (
                          <Tooltip label={(w.event_types ?? []).slice(3).join(', ')} position="top">
                            <Badge size="xs" variant="light">{t('common.plusMore', { count: rest })}</Badge>
                          </Tooltip>
                        )}
                      </Group>
                    );
                  }
                },
                {
                  accessor: 'secret_prefix',
                  title: t('settings.webhooks.columns.secret'),
                  // §4 item 9: after the one-time reveal there was no way to
                  // tell which secret a subscription was using.
                  render: (w) => w.secret_prefix
                    ? <code>{w.secret_prefix}…</code>
                    : <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>
                },
                {
                  accessor: 'failure_count',
                  title: t('settings.webhooks.columns.failures'),
                  // §4 item 8: a subscription failing forty times in a row
                  // looked identical to a healthy one.
                  render: (w) => (w.failure_count ?? 0) > 0
                    ? (
                      <Tooltip label={t('settings.webhooks.failuresHint')} position="top">
                        <Badge size="xs" color="red" variant="light">{w.failure_count}</Badge>
                      </Tooltip>
                    )
                    : <Text role="caption" c="var(--rr-color-text-muted)">0</Text>
                },
                {
                  accessor: 'is_active',
                  title: t('common.status'),
                  render: (w) => (
                    <StatusBadge status={w.is_active ? 'healthy' : 'inactive'}>
                      {w.is_active ? t('status.active') : t('status.paused')}
                    </StatusBadge>
                  )
                },
                {
                  accessor: 'created_at',
                  title: t('common.created'),
                  render: (w) => (
                    <Tooltip label={formatDateTime(w.created_at)} position="top">
                      <span>{formatRelative(w.created_at)}</span>
                    </Tooltip>
                  )
                },
                {
                  accessor: 'actions',
                  title: t('common.actions'),
                  render: (w) => (
                    <Menu>
                      <Menu.Target>
                        <Button size="xs" variant="subtle" aria-label={t('settings.webhooks.actions.for', { url: w.url })}>⋯</Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => setInspecting(w)}>{t('settings.webhooks.deliveries.title')}</Menu.Item>
                        <Menu.Item onClick={() => test.mutate(w.id)}>{t('settings.webhooks.actions.test')}</Menu.Item>
                        {/* §4 item 2, "the highest-impact missing capability":
                            PATCH was implemented and wrapped, and the page
                            offered only delete, so fixing a typo in a URL meant
                            delete-and-recreate, which rotates the signing
                            secret and breaks the receiver. */}
                        <Menu.Item onClick={() => setEditing(w)}>{t('action.edit')}</Menu.Item>
                        <Menu.Item onClick={() => setActive.mutate({ id: w.id, active: !w.is_active })}>
                          {w.is_active ? t('action.pause') : t('settings.webhooks.actions.resume')}
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item color="red" onClick={() => confirmDelete(w)}>{t('action.delete')}</Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )
                }
              ]}
            />
          )}
        </Stack>
      </Card>

      <WebhookDrawer
        subscription={creating ? 'new' : editing}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={(secret) => {
          if (secret) setFreshSecret(secret);
          qc.invalidateQueries({ queryKey: ['webhooks'] });
        }}
      />

      <DeliveriesDrawer subscription={inspecting} onClose={() => setInspecting(null)} />
    </Stack>
  );
}

function WebhookDrawer({
  subscription,
  onClose,
  onSaved
}: {
  subscription: webhooks.WebhookSubscription | 'new' | null;
  onClose: () => void;
  onSaved: (secret?: string) => void;
}) {
  const t = useT();
  const isNew = subscription === 'new';
  const existing = isNew || subscription === null ? null : subscription;
  const open = subscription !== null;

  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['message.received']);
  const [busy, setBusy] = useState(false);
  const [urlError, setUrlError] = useState('');

  const target = existing?.id ?? (isNew ? 'new' : null);
  useEffect(() => {
    if (!open) return;
    setUrl(existing?.url ?? '');
    setEvents(existing?.event_types?.length ? [...existing.event_types] : ['message.received']);
    setUrlError('');
  }, [open, target]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unknown event types on an existing subscription stay selectable, so
  // editing one does not silently drop an event this list has not heard of.
  const groups = useMemo(() => {
    const extra = events.filter((e) => !EVENT_TYPES.includes(e));
    return extra.length ? [...EVENT_GROUPS, { key: 'other', events: extra }] : EVENT_GROUPS;
  }, [events]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!/^https?:\/\/.+/i.test(url.trim())) {
      setUrlError(t('settings.webhooks.form.urlInvalid'));
      return;
    }
    if (events.length === 0) return;

    setBusy(true);
    try {
      if (existing) {
        await webhooks.updateWebhook(existing.id, { url: url.trim(), event_types: events });
        notifySuccess(t('settings.webhooks.toast.updated'), t('settings.nav.webhooks'));
        onSaved();
      } else {
        const res = await webhooks.createWebhook({ url: url.trim(), event_types: events });
        onSaved(res.data.secret);
      }
      onClose();
    } catch (err) {
      /*
       * §4 item 4: `isInvalidTarget` and `invalidTargetReason` exist for
       * exactly this and were unused, so the SSRF guard's refusal reached a
       * developer as a raw error string and they had no idea why a URL that
       * looks fine was rejected.
       */
      if (webhooks.isInvalidTarget(err)) {
        const reason = webhooks.invalidTargetReason(err);
        setUrlError(reason ? t(`settings.webhooks.blocked.${reason}`, reason) : t('settings.webhooks.form.urlRefused'));
      } else {
        notifyError(err instanceof Error ? err.message : t('common.unknownError'), t('settings.nav.webhooks'));
      }
    } finally {
      setBusy(false);
    }
  }

  function toggleEvent(value: string) {
    setEvents((prev) => prev.includes(value) ? prev.filter((e) => e !== value) : [...prev, value]);
  }

  return (
    <Drawer
      opened={open}
      onClose={onClose}
      title={existing ? t('settings.webhooks.actions.edit') : t('settings.webhooks.actions.add')}
      position="right"
      size="md"
    >
      <form onSubmit={save}>
        <Stack gap="md" style={{ padding: 4 }}>
          <TextInput
            label={t('settings.webhooks.columns.url')}
            description={t('settings.webhooks.form.urlHint')}
            error={urlError}
            value={url}
            onChange={(e) => { setUrl(e.currentTarget.value); setUrlError(''); }}
            placeholder="https://example.com/hooks/repletoryreach"
            required
            autoFocus
          />

          <Stack gap={4}>
            <Text role="inputLabel">{t('settings.webhooks.columns.events')}</Text>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.webhooks.form.eventsHint')}</Text>
            <Stack gap="md">
              {groups.map((g) => (
                <div key={g.key} role="group" aria-label={t(`settings.webhooks.group.${g.key}`)} data-testid={`webhook-group-${g.key}`}>
                  <Stack gap={6}>
                    <Text role="caption" fw={600}>{t(`settings.webhooks.group.${g.key}`)}</Text>
                    {g.events.map((e) => (
                      <Switch
                        key={e}
                        label={e}
                        description={t(`settings.webhooks.event.${e}`, '')}
                        checked={events.includes(e)}
                        onChange={() => toggleEvent(e)}
                      />
                    ))}
                  </Stack>
                </div>
              ))}
            </Stack>
          </Stack>

          {existing && (
            <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.webhooks.form.secretKept')}</Text>
          )}

          <Group justify="flex-end" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
            <Button type="button" variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" loading={busy} disabled={!url.trim() || events.length === 0}>
              {existing ? t('action.saveChanges') : t('settings.webhooks.actions.add')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
